<script setup>
import {computed, ref, watch} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useModalsStore} from '@/stores/modals'
import {useItemDataStore} from '@/stores/itemData'
import FormControlCategoryTitle from '@/common/form-control/FormControlCategoryTitle.vue'
import Button from '@/common/Button.vue'
import ItemStatusIcons from "@/common/ItemStatusIcons.vue"

const props = defineProps({field: Object, item: Object})

const componentsStore = useComponentsStore()
const modalsStore = useModalsStore()
const itemDataStore = useItemDataStore()

const content = ref(null)

watch(
    () => props.item.contentId,
    async (newContentId) => {
        if (!newContentId) {
            content.value = null
            return
        }
        content.value = await itemDataStore.get({
            tableName: 'content',
            id: newContentId
        })
    },
    {immediate: true}
)

const contentType = ref(null)

watch(
    content,
    async (newContent) => {
        if (!newContent) {
            contentType.value = null
            return
        }
        contentType.value = await itemDataStore.get({
            tableName: 'templates_templates',
            id: newContent.contentType
        })
    },
    {immediate: true}
)

const componentId = computed(() => contentType.value?.componentId)

const tableName = computed(() => {
    const targetId = props.item.fieldId === null ? componentId.value : props.field?.categoryComponentId
    return componentsStore.items.find(o => o.id === targetId)?.tableName
})

const itemIds = computed({
    get: () => props.item.value,
    set: (val) => {
        props.item.value = val
    }
})

const openSelectListModal = () => {
    modalsStore.show({
        componentName: 'SelectListModal',
        obj: {
            listingName: `${tableName.value}.${props.item.fieldId}`,
            selectedIds: itemIds.value || [],
            onSelect: (ids) => {
                itemIds.value = ids
            }
        }
    })
}

const removeItem = (id) => {
    itemIds.value = itemIds.value.filter(o => o !== parseInt(id))
}

const moveUp = (i) => moveItem(i, -1)
const moveDown = (i) => moveItem(i, 1)

const moveItem = (i, dir) => {
    const target = i + dir
    const ids = [...itemIds.value]
    if (target < 0 || target >= ids.length) return;
    [ids[i], ids[target]] = [ids[target], ids[i]]
    itemIds.value = ids
}
</script>

<template>
    <div v-if="tableName" class="w-100">
        <div v-for="(itemId, i) in itemIds" :key="i"
             class="mb-1 text-bg-light rounded p-1 small d-flex align-items-center">
            <span class="me-2 text-secondary">{{ i + 1 }}.</span>
            <FormControlCategoryTitle :table-name="tableName" :id="parseInt(itemId)" class="flex-fill"/>
            <ItemStatusIcons :id="itemId" :table-name="tableName"/>
            <Button :disabled="i === 0" @click="moveUp(i)" class="ms-2 btn-sm border"><i
                class="bi bi-chevron-up"/></Button>
            <Button :disabled="i === itemIds.length - 1" @click="moveDown(i)" class="ms-2 btn-sm border"><i
                class="bi bi-chevron-down"/></Button>
            <Button @click="removeItem(itemId)" class="ms-2 btn-sm border"><i class="bi bi-x"/></Button>
        </div>
        <Button @click="openSelectListModal" class="btn-sm border"><i class="bi bi-plus-lg"/></Button>
    </div>
    <small v-else-if="!componentId" class="text-danger form-text ms-2 mb-1 fst-italic small">Error: No collection
        targeted</small>
</template>